// MissionPeriod component: Manages the mission search filters, results display, and detail view.
import MissionDisplay from 'components/Mission/MissionDisplay';
import Badge from 'components/badge/badge';
import {
  Building,
  Calendar,
  CogIcon,
  CrossIcon,
  Filter,
  PersonIcon,
  ReferenceIcon,
  SaveIcon,
  Signer,
} from '../../../assets/icons';
import AdminSearchInput from '../../../components/AdminSearch/AdminSearchInput';
import Tooltip from '../../../components/tooltips/Tooltips';
import EmptyState from '../../../components/EmptyState/EmptyState';
import Button from '../../../components/button';
import PageForm from '../../../components/pageForm/PageForm';
import SubNavbar from '../../../components/SubNavbar/SubNavbar';
import StyledInput from '../../../components/styledInput/StyledInput';
import FilterDropdown from '../../../components/filter/FilterDropdown';
import BottomSheet from '../../../components/bottomSheet/BottomSheet';
import MediaQuery from 'react-responsive';
import { useEffect, useMemo, useState, forwardRef, useRef } from 'react';
import DatePicker from 'react-datepicker';
import ApiDatabase from '../../../server';
import { RECRUITMENT_FILTER_OPTIONS } from '../../../constants/missionRecruitmentFilters';
import { CONTRACT_FILTER_OPTIONS } from '../../../constants/missionContractFilters';
import { MISSION_STATUS_FILTER_OPTIONS } from '../../../constants/missionStatusFilters';
import {
  buildSupplierFilterOptions,
  getSupplierFilterLabel,
} from '../../../constants/missionSupplierFilters';
import {
  MISSION_NAVBAR_FILTER_DEFINITIONS,
  MISSION_NAVBAR_FILTERS_DEFAULT,
  MISSION_NAVBAR_FILTERS_STORAGE_KEY,
} from '../../../components/modal/dataModal/data/ModalMission/ModalCustomizeMissionNavbarFilters';
import useModal from '../../../components/modal/useModal';
import UseContext from '../../../hooks/useContext';
import { generateEmployeeColor } from '../../../helper/helper';
import useMissionPeriod from '../../../hooks/useMissionPeriod';
import MissionFilterPanel from './MissionFilterPanel';

const MissionDateRangeTrigger = forwardRef(({ onClick }, ref) => (
  <span ref={ref} className="inline-flex">
    <Button
      size="ICON"
      color="SECONDARY"
      onClick={onClick}
      className="h-10 w-10 p-0 !border-0 !rounded-none bg-white hover:bg-gray-100"
    >
      <Calendar wh={20} color="#374151" />
    </Button>
  </span>
));
MissionDateRangeTrigger.displayName = 'MissionDateRangeTrigger';

const getMissionSearchOptionName = (option) => {
  if (option.name) return option.name;
  if (option.firstname || option.lastname) {
    return [option.firstname, option.lastname].filter(Boolean).join(' ');
  }
  if (typeof option.label === 'string') {
    return option.label.replace(/\s*\([^)]*\)\s*$/, '').trim();
  }
  return String(option.value || '');
};

const buildMissionSearchOptionLabel = (option) => {
  const name = getMissionSearchOptionName(option);
  const idBestt = option.id_bestt;

  return (
    <span className="inline-flex items-baseline gap-1 min-w-0">
      <span className="truncate">{name}</span>
      {idBestt ? <span className="text-xs text-gray-500 shrink-0">N°{idBestt}</span> : null}
    </span>
  );
};

const MissionSearchSelectFilter = ({
  selected,
  onChange,
  placeholder,
  apiMethod,
  fullWidth = false,
  maxWidth = 'max-w-[180px]',
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const trimmedSearch = searchValue.trim();
    if (trimmedSearch.length < 2) {
      setOptions([]);
      setIsLoading(false);
      return undefined;
    }

    let isCancelled = false;
    const timer = setTimeout(() => {
      setIsLoading(true);
      ApiDatabase[apiMethod](
        { search: trimmedSearch, limit: 10 },
        (data) => {
          if (isCancelled) return;
          setOptions(Array.isArray(data) ? data : []);
          setIsLoading(false);
        },
        () => {
          if (isCancelled) return;
          setOptions([]);
          setIsLoading(false);
        }
      );
    }, 300);

    return () => {
      isCancelled = true;
      clearTimeout(timer);
    };
  }, [apiMethod, searchValue]);

  const trimmedSearch = searchValue.trim();
  const isActiveSearch = trimmedSearch.length >= 2;

  const list = useMemo(() => {
    const selectedItems = selected.map((item) => ({
      value: item.value,
      name: item.name || getMissionSearchOptionName(item),
      id_bestt: item.id_bestt,
      label: buildMissionSearchOptionLabel(item),
      checked: true,
    }));

    const searchItems = options
      .filter((option) => !selected.some((item) => item.value === option.value))
      .map((option) => ({
        value: option.value,
        name: getMissionSearchOptionName(option),
        id_bestt: option.id_bestt,
        label: buildMissionSearchOptionLabel(option),
        checked: false,
      }));

    return [...selectedItems, ...searchItems];
  }, [selected, options]);

  const visibleList = useMemo(() => {
    const selectedItems = list.filter((item) =>
      selected.some((selectedItem) => selectedItem.value === item.value)
    );

    if (!isActiveSearch) {
      return selectedItems;
    }

    if (isLoading) {
      return selectedItems;
    }

    const searchItems = list.filter(
      (item) => !selected.some((selectedItem) => selectedItem.value === item.value)
    );

    return [...selectedItems, ...searchItems];
  }, [list, selected, isActiveSearch, isLoading]);

  const handleChange = (updatedList) => {
    onChange(
      updatedList
        .filter((item) => item.checked)
        .map((item) => {
          const fullItem = list.find((entry) => entry.value === item.value) || item;
          return {
            value: fullItem.value,
            name: fullItem.name || getMissionSearchOptionName(fullItem),
            ...(fullItem.id_bestt ? { id_bestt: fullItem.id_bestt } : {}),
          };
        })
    );
  };

  const dropdownFooter = (() => {
    if (isActiveSearch) {
      if (isLoading) {
        return <div className="text-sm text-gray-400">Recherche...</div>;
      }
      if (options.length === 0) {
        return <div className="text-sm text-gray-400">Aucun résultat</div>;
      }
      return null;
    }
    if (selected.length === 0) {
      return <div className="text-sm text-gray-400">Saisissez au moins 2 caractères</div>;
    }
    return null;
  })();

  return (
    <FilterDropdown
      maxWidth={fullWidth ? 'w-full' : maxWidth}
      value={
        selected.length > 0
          ? selected.map((item) => item.name || getMissionSearchOptionName(item)).join(', ')
          : placeholder
      }
      list={list}
      visibleList={visibleList}
      onChange={handleChange}
      type="selectMultipleCheckbox"
      isSearchable
      onSearchChange={setSearchValue}
      classNameValue="text-sm h-10 px-4 flex items-center justify-start"
      additionalClassName="h-10"
      dropdownFullWidth={fullWidth}
      footer={dropdownFooter}
    />
  );
};

const buildEmployeeFilterList = (employees) =>
  employees.map((employee) => ({
    value: employee._id,
    searchLabel: `${employee.firstname || ''} ${employee.lastname || ''}`.trim(),
    label: (
      <div className="flex gap-1.5 items-center">
        {employee._id !== 'unassign' && (
          <div
            className="rounded-full w-[24px] h-[24px] flex items-center justify-center text-[10px] text-white"
            style={{
              backgroundColor: generateEmployeeColor(employee.firstname, employee.lastname),
            }}
          >
            {`${employee.firstname?.charAt(0).toUpperCase() || ''}${employee.lastname?.charAt(0).toUpperCase() || ''}`}
          </div>
        )}
        <div>
          {employee.firstname} {employee.lastname}
        </div>
      </div>
    ),
    checked: employee.checked,
  }));

const MissionPeriod = () => {
  const { toggle } = useModal();
  const { setModalData, modalDataReturn } = UseContext();

  // State for mobile filter bottom sheet
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(false);
  const [isMoreFiltersOpen, setIsMoreFiltersOpen] = useState(false);
  const [visibleNavbarFilters, setVisibleNavbarFilters] = useState(() => {
    try {
      const saved = localStorage.getItem(MISSION_NAVBAR_FILTERS_STORAGE_KEY);
      return saved ? JSON.parse(saved) : MISSION_NAVBAR_FILTERS_DEFAULT;
    } catch {
      return MISSION_NAVBAR_FILTERS_DEFAULT;
    }
  });
  
  // Temporary filter states for bottom sheet (apply on "Appliquer" button)
  const [tempSearch, setTempSearch] = useState('');
  const [tempCompanySearch, setTempCompanySearch] = useState([]);
  const [tempAssignedTempWorkerSearch, setTempAssignedTempWorkerSearch] = useState([]);
  const [tempRecruitmentSearch, setTempRecruitmentSearch] = useState([]);
  const [tempSupplierSearch, setTempSupplierSearch] = useState([]);
  const [tempContractSearch, setTempContractSearch] = useState([]);
  const [tempStatusSearch, setTempStatusSearch] = useState([]);
  const [tempMissionDateRange, setTempMissionDateRange] = useState([null, null]);
  const [companySuppliers, setCompanySuppliers] = useState([]);

  // Destructure values and actions from the custom hook
  const {
    search,
    setSearch,
    missionDateRange,
    setMissionDateRange,
    missionDateStart,
    missionDateEnd,
    searchMissionResult,
    searchMissionResultWaiting,
    companySearch,
    setCompanySearch,
    assignedTempWorkerSearch,
    setAssignedTempWorkerSearch,
    recruitmentSearch,
    updateRecruitmentSearch,
    supplierSearch,
    updateSupplierSearch,
    contractSearch,
    updateContractSearch,
    departments,
    updateDepartment,
    missionPeriod,
    updateMissionPeriod,
    statusSearch,
    updateStatusSearch,
    employees,
    employeesSearch,
    updateEmployee,
    updateEmployeeSearch,
    openCompanyInfo,
    openTempWorkerInfo,
    handleDeleteMission,
    handleCancelMission,
    handleSelectMission,
    selectedMissions,
    shouldShowTrashButton,
    updateEmployeeForMission,
    currentPage,
    setCurrentPage,
    totalPages,
    countPerPage,
    countTotal,
    editMission,
    setEditMission,
    setSearchMissionResult,
    hasFilterChanges,
    getHasFilterChanges,
    saveDefaultFilters,
    hiddenByFiltersCount,
    resetFiltersToDefault,
  } = useMissionPeriod();

  useEffect(() => {
    ApiDatabase.getCompanySuppliers(
      {},
      (data) => setCompanySuppliers(Array.isArray(data) ? data : []),
      (err) => {
        if (err.name !== 'CanceledError') {
          console.error(err);
        }
      }
    );
  }, []);

  const filtersScrollRef = useRef(null);

  const handleFiltersWheel = (event) => {
    if (event.target.closest('.dropDown')) return;

    const container = filtersScrollRef.current;
    if (!container || event.deltaY === 0) return;
    if (container.scrollWidth > container.clientWidth) {
      event.preventDefault();
      container.scrollLeft += event.deltaY;
    }
  };

  const mobileFilterOverrides = useMemo(
    () =>
      isFilterSheetOpen
        ? {
            companySearch: tempCompanySearch,
            assignedTempWorkerSearch: tempAssignedTempWorkerSearch,
            recruitmentSearch: tempRecruitmentSearch,
            supplierSearch: tempSupplierSearch,
            contractSearch: tempContractSearch,
            statusSearch: tempStatusSearch,
          }
        : {},
    [
      isFilterSheetOpen,
      tempCompanySearch,
      tempAssignedTempWorkerSearch,
      tempRecruitmentSearch,
      tempSupplierSearch,
      tempContractSearch,
      tempStatusSearch,
    ]
  );

  const mobileHasFilterChanges = getHasFilterChanges(mobileFilterOverrides);

  const handleMobileSaveFilters = () => {
    saveDefaultFilters(mobileFilterOverrides);
  };

  const activeFilterCount = useMemo(() => {
    const filters = [
      Boolean(missionDateStart || missionDateEnd),
      companySearch.length > 0,
      assignedTempWorkerSearch.length > 0,
      recruitmentSearch.length > 0,
      supplierSearch.length > 0,
      contractSearch.length > 0,
      departments.some((department) => department.checked),
      missionPeriod.some((period) => period.checked),
      statusSearch.length > 0,
      employeesSearch.some((employee) => employee.checked),
    ];

    return filters.filter(Boolean).length;
  }, [
    missionDateStart,
    missionDateEnd,
    companySearch,
    assignedTempWorkerSearch,
    recruitmentSearch,
    supplierSearch,
    contractSearch,
    departments,
    missionPeriod,
    statusSearch,
    employeesSearch,
  ]);

  const hiddenActiveFilterCount = useMemo(() => {
    const activeByKey = {
      dateRange: Boolean(missionDateStart || missionDateEnd),
      company: companySearch.length > 0,
      assignedTempWorker: assignedTempWorkerSearch.length > 0,
      recruitment: recruitmentSearch.length > 0,
      supplier: supplierSearch.length > 0,
      contract: contractSearch.length > 0,
      department: departments.some((department) => department.checked),
      period: missionPeriod.some((period) => period.checked),
      status: statusSearch.length > 0,
      employee: employeesSearch.some((employee) => employee.checked),
    };

    return Object.entries(activeByKey).filter(
      ([key, isActive]) => isActive && !visibleNavbarFilters.includes(key)
    ).length;
  }, [
    missionDateStart,
    missionDateEnd,
    companySearch,
    assignedTempWorkerSearch,
    recruitmentSearch,
    supplierSearch,
    contractSearch,
    departments,
    missionPeriod,
    statusSearch,
    employeesSearch,
    visibleNavbarFilters,
  ]);

  useEffect(() => {
    if (modalDataReturn?.type === 'mission_navbar_filters_updated') {
      setVisibleNavbarFilters(modalDataReturn.visibleNavbarFilters);
    }
  }, [modalDataReturn]);

  // Open filter sheet and sync temporary states
  const openFilterSheet = () => {
    setTempSearch(search);
    setTempMissionDateRange(missionDateRange);
    setTempCompanySearch(companySearch);
    setTempAssignedTempWorkerSearch(assignedTempWorkerSearch);
    setTempRecruitmentSearch(recruitmentSearch);
    setTempSupplierSearch(supplierSearch);
    setTempContractSearch(contractSearch);
    setTempStatusSearch(statusSearch);
    setIsFilterSheetOpen(true);
  };

  const applyFilters = () => {
    setSearch(tempSearch);
    setMissionDateRange(tempMissionDateRange);
    setCompanySearch(tempCompanySearch);
    setAssignedTempWorkerSearch(tempAssignedTempWorkerSearch);
    updateRecruitmentSearch(tempRecruitmentSearch);
    updateSupplierSearch(tempSupplierSearch);
    updateContractSearch(tempContractSearch);
    updateStatusSearch(tempStatusSearch);
    setIsFilterSheetOpen(false);
  };

  const getRecruitmentFilterValue = (selected = recruitmentSearch) => {
    if (selected.length === 0) return 'Recrutement';
    return RECRUITMENT_FILTER_OPTIONS.filter((option) => selected.includes(option.type))
      .map((option) => option.name)
      .join(', ');
  };

  const buildRecruitmentFilterList = (selected = recruitmentSearch) =>
    RECRUITMENT_FILTER_OPTIONS.map((option) => ({
      value: option.type,
      label: (
        <div className="flex flex-col">
          <span>{option.name}</span>
          {option.description && (
            <span className="text-xs text-gray-500 font-normal">{option.description}</span>
          )}
        </div>
      ),
      checked: selected.includes(option.type),
    }));

  const buildSupplierFilterList = (selected = supplierSearch) =>
    buildSupplierFilterOptions(companySuppliers).map((option) => ({
      value: option.value,
      label: option.name,
      checked: selected.includes(option.value),
    }));

  const handleSingleSelectFilterChange = (onSelectedChange) => (updatedList) => {
    const selectedType = updatedList.find((item) => item.checked)?.value;
    onSelectedChange(selectedType ? [selectedType] : []);
  };

  const getContractFilterValue = (selected = contractSearch) => {
    const activeType = selected[0];
    if (!activeType) return 'Contrat';
    return CONTRACT_FILTER_OPTIONS.find((option) => option.type === activeType)?.name
      || 'Contrat';
  };

  const buildContractFilterList = (selected = contractSearch) => {
    const activeType = selected[0];
    return CONTRACT_FILTER_OPTIONS.map((option) => ({
      value: option.type,
      label: (
        <div className="flex flex-col">
          <span>{option.name}</span>
          {option.description && (
            <span className="text-xs text-gray-500 font-normal">{option.description}</span>
          )}
        </div>
      ),
      checked: option.type === activeType,
    }));
  };

  const renderContractFilter = (
    selected = contractSearch,
    onSelectedChange = updateContractSearch,
    fullWidth = false
  ) => (
    <FilterDropdown
      maxWidth={fullWidth ? 'w-full' : 'max-w-[220px]'}
      value={getContractFilterValue(selected)}
      list={buildContractFilterList(selected)}
      onChange={handleSingleSelectFilterChange(onSelectedChange)}
      type="selectMultipleRadio"
      classNameValue="text-sm h-10 px-4 flex items-center justify-start"
      additionalClassName="h-10"
    />
  );

  const getCheckedNames = (items) =>
    items.filter((item) => item.checked).map((item) => item.name).join(', ');

  const getEmployeeFilterValue = () =>
    employeesSearch.filter((employee) => employee.checked).length > 0
      ? employeesSearch
          .filter((employee) => employee.checked)
          .map((employee) => `${employee.firstname} ${employee.lastname}`)
          .join(', ')
      : 'Admin assigné';

  const renderDateRangeFilter = (
    dateRange = missionDateRange,
    onChange = setMissionDateRange,
    fullWidth = false
  ) => {
    if (fullWidth) {
      return (
        <div className="relative w-full">
          <div className="absolute left-0 top-0 h-10 flex items-center pl-2.5 pointer-events-none z-[1]">
            <Calendar wh={16} color="#6B7280" />
          </div>
          <DatePicker
            selectsRange
            startDate={dateRange[0]}
            endDate={dateRange[1]}
            onChange={onChange}
            isClearable
            placeholderText="Période"
            className="w-full h-10 pl-9 pr-8 ft-b border rounded-md text-left text-sm focus:border-yellow-500 focus:outline-none"
            locale="fr-FR"
            dateFormat="dd/MM/yyyy"
            popperPlacement="bottom-start"
            popperProps={{ strategy: 'fixed' }}
          />
        </div>
      );
    }

    const hasSelection = Boolean(dateRange[0] || dateRange[1]);

    return (
      <div
        className={`inline-flex items-stretch h-10 border rounded-md bg-white overflow-hidden shrink-0 ${
          fullWidth ? 'w-full' : ''
        }`}
      >
        <DatePicker
          selectsRange
          startDate={dateRange[0]}
          endDate={dateRange[1]}
          onChange={onChange}
          customInput={<MissionDateRangeTrigger />}
          wrapperClassName="inline-flex"
          locale="fr-FR"
          dateFormat="dd/MM/yyyy"
          popperPlacement="bottom-start"
          popperProps={{ strategy: 'fixed' }}
        />
        {hasSelection && (
          <Button
            size="ICON"
            color="SECONDARY"
            className="h-10 w-10 p-0 shrink-0 !border-0 !rounded-none border-l bg-white hover:bg-gray-100"
            onClick={() => onChange([null, null])}
          >
            <CrossIcon wh={16} color="#374151" />
          </Button>
        )}
      </div>
    );
  };

  const renderCompanyFilter = (
    selected = companySearch,
    onSelectedChange = setCompanySearch,
    fullWidth = false
  ) => (
    <MissionSearchSelectFilter
      selected={selected}
      onChange={onSelectedChange}
      placeholder="Entreprise"
      apiMethod="searchMissionCompanies"
      fullWidth={fullWidth}
    />
  );

  const renderAssignedTempWorkerFilter = (
    selected = assignedTempWorkerSearch,
    onSelectedChange = setAssignedTempWorkerSearch,
    fullWidth = false
  ) => (
    <MissionSearchSelectFilter
      selected={selected}
      onChange={onSelectedChange}
      placeholder="Intérimaire"
      apiMethod="searchMissionAssignedTempWorkers"
      fullWidth={fullWidth}
      maxWidth="max-w-[175px]"
    />
  );

  const renderRecruitmentFilter = (
    selected = recruitmentSearch,
    onSelectedChange = updateRecruitmentSearch,
    fullWidth = false
  ) => (
    <FilterDropdown
      maxWidth={fullWidth ? 'w-full' : 'max-w-[180px]'}
      value={getRecruitmentFilterValue(selected)}
      list={buildRecruitmentFilterList(selected)}
      onChange={handleMultiSelectFilterChange(onSelectedChange)}
      type="selectMultipleCheckbox"
      classNameValue="text-sm h-10 px-4 flex items-center justify-start"
      additionalClassName="h-10"
    />
  );

  const renderSupplierFilter = (
    selected = supplierSearch,
    onSelectedChange = updateSupplierSearch,
    fullWidth = false
  ) => (
    <FilterDropdown
      maxWidth={fullWidth ? 'w-full' : 'max-w-[180px]'}
      value={getSupplierFilterLabel(selected, companySuppliers)}
      list={buildSupplierFilterList(selected)}
      onChange={handleMultiSelectFilterChange(onSelectedChange)}
      type="selectMultipleCheckbox"
      classNameValue="text-sm h-10 px-4 flex items-center justify-start"
      additionalClassName="h-10"
    />
  );

  const renderDepartmentFilter = (fullWidth = false) => (
    <FilterDropdown
      maxWidth={fullWidth ? 'w-full' : 'max-w-[175px]'}
      value="Département"
      list={departments.map((department) => ({
        value: department.name,
        label: department.name,
        checked: department.checked,
      }))}
      onChange={updateDepartment}
      type="selectMultipleCheckbox"
      classNameValue="text-sm h-10 px-4 flex items-center justify-start"
      additionalClassName="h-10"
    />
  );

  const renderMissionPeriodFilter = (fullWidth = false) => (
    <FilterDropdown
      maxWidth={fullWidth ? 'w-full' : 'max-w-[155px]'}
      value={getCheckedNames(missionPeriod) || 'Statut'}
      list={missionPeriod.map((p) => ({
        value: p.type,
        label: p.name,
        checked: p.checked,
      }))}
      onChange={updateMissionPeriod}
      type="selectMultipleCheckbox"
      classNameValue="text-sm h-10 px-4 flex items-center justify-start"
      additionalClassName="h-10"
    />
  );

  const handleMultiSelectFilterChange = (onSelectedChange) => (updatedList) => {
    onSelectedChange(updatedList.filter((item) => item.checked).map((item) => item.value));
  };

  const getStatusFilterValue = (selected = statusSearch) => {
    if (selected.length === 0) return 'Visibilité';
    return MISSION_STATUS_FILTER_OPTIONS.filter((option) => selected.includes(option.type))
      .map((option) => option.name)
      .join(', ');
  };

  const renderMissionStatusFilter = (
    selected = statusSearch,
    onSelectedChange = updateStatusSearch,
    fullWidth = false
  ) => (
    <FilterDropdown
      maxWidth={fullWidth ? 'w-full' : 'max-w-[170px]'}
      value={getStatusFilterValue(selected)}
      list={MISSION_STATUS_FILTER_OPTIONS.map((option) => ({
        value: option.type,
        label: (
          <div className="flex flex-col">
            <span>{option.name}</span>
            {option.description && (
              <span className="text-xs text-gray-500 font-normal">{option.description}</span>
            )}
          </div>
        ),
        checked: selected.includes(option.type),
      }))}
      onChange={handleMultiSelectFilterChange(onSelectedChange)}
      type="selectMultipleCheckbox"
      classNameValue="text-sm h-10 px-4 flex items-center justify-start"
      additionalClassName="h-10"
    />
  );

  const renderEmployeeFilter = (fullWidth = false) => (
    <FilterDropdown
      maxWidth={fullWidth ? 'w-full' : 'max-w-[180px]'}
      value={getEmployeeFilterValue()}
      list={buildEmployeeFilterList(employeesSearch)}
      onChange={updateEmployeeSearch}
      type="selectMultipleCheckbox"
      isSearchable
      dropdownFullWidth={fullWidth}
      additionalClassNameDropDown="text-gray-900"
      classNameValue="text-sm h-10 px-4 flex items-center justify-start"
      additionalClassName="h-10"
    />
  );

  const formatMissionFilterDate = (date) => {
    if (!date) return '';
    const parsedDate = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(parsedDate.getTime())) return '';
    return parsedDate.toLocaleDateString('fr-FR');
  };

  const getMissionFilterDisplayValue = (key) => {
    switch (key) {
      case 'dateRange':
        if (!missionDateStart && !missionDateEnd) return null;
        if (missionDateStart && missionDateEnd) {
          return `${formatMissionFilterDate(missionDateStart)} → ${formatMissionFilterDate(missionDateEnd)}`;
        }
        if (missionDateStart) return `À partir du ${formatMissionFilterDate(missionDateStart)}`;
        return `Jusqu'au ${formatMissionFilterDate(missionDateEnd)}`;
      case 'company':
        return companySearch.length > 0
          ? companySearch.map((item) => item.name || getMissionSearchOptionName(item)).join(', ')
          : null;
      case 'assignedTempWorker':
        return assignedTempWorkerSearch.length > 0
          ? assignedTempWorkerSearch
              .map((item) => item.name || getMissionSearchOptionName(item))
              .join(', ')
          : null;
      case 'recruitment':
        return recruitmentSearch.length > 0 ? getRecruitmentFilterValue(recruitmentSearch) : null;
      case 'supplier':
        return supplierSearch.length > 0
          ? getSupplierFilterLabel(supplierSearch, companySuppliers)
          : null;
      case 'contract':
        return contractSearch.length > 0 ? getContractFilterValue(contractSearch) : null;
      case 'department': {
        const checkedDepartments = getCheckedNames(departments);
        return checkedDepartments || null;
      }
      case 'period': {
        const checkedPeriods = getCheckedNames(missionPeriod);
        return checkedPeriods || null;
      }
      case 'status':
        return statusSearch.length > 0 ? getStatusFilterValue(statusSearch) : null;
      case 'employee': {
        const checkedEmployees = employeesSearch.filter((employee) => employee.checked);
        return checkedEmployees.length > 0
          ? checkedEmployees.map((employee) => `${employee.firstname} ${employee.lastname}`).join(', ')
          : null;
      }
      default:
        return null;
    }
  };

  const clearMissionFilterByKey = (key) => {
    switch (key) {
      case 'dateRange':
        setMissionDateRange([null, null]);
        break;
      case 'company':
        setCompanySearch([]);
        break;
      case 'assignedTempWorker':
        setAssignedTempWorkerSearch([]);
        break;
      case 'recruitment':
        updateRecruitmentSearch([]);
        break;
      case 'supplier':
        updateSupplierSearch([]);
        break;
      case 'contract':
        updateContractSearch([]);
        break;
      case 'department':
        updateDepartment(
          departments.map((department) => ({
            value: department.name,
            label: department.name,
            checked: false,
          }))
        );
        break;
      case 'period':
        updateMissionPeriod(
          missionPeriod.map((period) => ({
            value: period.type,
            label: period.name,
            checked: false,
          }))
        );
        break;
      case 'status':
        updateStatusSearch([]);
        break;
      case 'employee':
        updateEmployeeSearch(
          employeesSearch.map((employee) => ({
            value: employee._id,
            label: `${employee.firstname} ${employee.lastname}`,
            checked: false,
          }))
        );
        break;
      default:
        break;
    }
  };

  const renderFilterByKey = (key, options = {}) => {
    const {
      recruitmentValue = recruitmentSearch,
      onRecruitmentChange = updateRecruitmentSearch,
      supplierValue = supplierSearch,
      onSupplierChange = updateSupplierSearch,
      contractValue = contractSearch,
      onContractChange = updateContractSearch,
      companyValue = companySearch,
      onCompanyChange = setCompanySearch,
      assignedTempWorkerValue = assignedTempWorkerSearch,
      onAssignedTempWorkerChange = setAssignedTempWorkerSearch,
      statusValue = statusSearch,
      onStatusChange = updateStatusSearch,
      dateRangeValue = missionDateRange,
      onDateRangeChange = setMissionDateRange,
      fullWidth = false,
    } = options;

    const filters = {
      dateRange: renderDateRangeFilter(dateRangeValue, onDateRangeChange, fullWidth),
      company: renderCompanyFilter(companyValue, onCompanyChange, fullWidth),
      assignedTempWorker: renderAssignedTempWorkerFilter(
        assignedTempWorkerValue,
        onAssignedTempWorkerChange,
        fullWidth
      ),
      recruitment: renderRecruitmentFilter(recruitmentValue, onRecruitmentChange, fullWidth),
      supplier: renderSupplierFilter(supplierValue, onSupplierChange, fullWidth),
      contract: renderContractFilter(contractValue, onContractChange, fullWidth),
      department: renderDepartmentFilter(fullWidth),
      period: renderMissionPeriodFilter(fullWidth),
      status: renderMissionStatusFilter(statusValue, onStatusChange, fullWidth),
      employee: renderEmployeeFilter(fullWidth),
    };

    return filters[key] || null;
  };

  return (
    <PageForm>
      <>
        <SubNavbar containedLayout>
          <div className="flex items-start gap-3 min-w-0 w-full text-sm">
            <MediaQuery minWidth={768}>
              <div className="flex flex-1 min-w-0">
                <div
                  ref={filtersScrollRef}
                  onWheel={handleFiltersWheel}
                  className="w-full overflow-x-auto overflow-y-hidden thin-scrollbar"
                >
                  <div className="flex h-10 items-center gap-2 w-max">
                    <div className="shrink-0 h-10 w-[15rem] min-w-[8rem]">
                      <AdminSearchInput
                        className="w-full"
                        placeholder="Rechercher..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </div>
                    {visibleNavbarFilters.map((filterKey) => (
                      <div key={filterKey} className="shrink-0">
                        {renderFilterByKey(filterKey)}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex shrink-0 h-10 items-center gap-2">
                <Tooltip text="Modifier la barre de filtres" placement="bottom">
                  <Button
                    size="ICON"
                    color="SECONDARY"
                    onClick={() => {
                      setModalData({ visibleNavbarFilters });
                      toggle('mission_customize_navbar_filters');
                    }}
                  >
                    <CogIcon wh={20} color="#374151" />
                  </Button>
                </Tooltip>
                <Tooltip text="Sauvegarder les filtres" placement="bottom" disabled={!hasFilterChanges}>
                  <Button
                    color={hasFilterChanges ? 'SECONDARY' : 'DISABLED'}
                    size="SMI"
                    disabled={!hasFilterChanges}
                    onClick={saveDefaultFilters}
                  >
                    <SaveIcon wh={20} color={hasFilterChanges ? '#374151' : '#9CA3AF'} />
                  </Button>
                </Tooltip>
                <Button color="SECONDARY" size="SM" onClick={() => setIsMoreFiltersOpen(true)}>
                  <Filter wh={16} color="#374151" />
                  <span>Plus de filtres</span>
                  {hiddenActiveFilterCount > 0 && <Badge type="WARNING">{hiddenActiveFilterCount}</Badge>}
                </Button>
              </div>
            </MediaQuery>

            <MediaQuery maxWidth={767}>
              <Button
                color={'SECONDARY'}
                size={'SM'}
                onClick={openFilterSheet}
              >
                <Filter wh={16} color="#374151" />
                <span>Filtrer</span>
                {activeFilterCount > 0 && <Badge type="WARNING">{activeFilterCount}</Badge>}
              </Button>
            </MediaQuery>
          </div>
        </SubNavbar>

        <MissionFilterPanel
          isOpen={isMoreFiltersOpen}
          onClose={() => setIsMoreFiltersOpen(false)}
          activeFilterCount={activeFilterCount}
          filters={MISSION_NAVBAR_FILTER_DEFINITIONS}
          renderFilterByKey={renderFilterByKey}
          getFilterDisplayValue={getMissionFilterDisplayValue}
          clearFilterByKey={clearMissionFilterByKey}
          onReset={resetFiltersToDefault}
          onApply={() => setIsMoreFiltersOpen(false)}
        />

        {/* Mobile: BottomSheet for filters */}
        <BottomSheet
          isOpen={isFilterSheetOpen}
          onClose={() => setIsFilterSheetOpen(false)}
          title="Filtres"
        >
          <div className="flex flex-col gap-4">
            {/* Search input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rechercher</label>
              <AdminSearchInput
                placeholder="Rechercher..."
                value={tempSearch}
                onChange={(e) => setTempSearch(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Période</label>
              {renderDateRangeFilter(tempMissionDateRange, setTempMissionDateRange, true)}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Entreprise</label>
              {renderCompanyFilter(tempCompanySearch, setTempCompanySearch, true)}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Intérimaire</label>
              {renderAssignedTempWorkerFilter(
                tempAssignedTempWorkerSearch,
                setTempAssignedTempWorkerSearch,
                true
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Recrutement</label>
              {renderRecruitmentFilter(tempRecruitmentSearch, setTempRecruitmentSearch)}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fournisseur</label>
              {renderSupplierFilter(tempSupplierSearch, setTempSupplierSearch, true)}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contrat</label>
              {renderContractFilter(tempContractSearch, setTempContractSearch, true)}
            </div>

            {/* Departments */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Département</label>
              <FilterDropdown
                maxWidth="w-full"
                value="Sélectionner"
                list={departments.map((department) => ({
                  value: department.name,
                  label: department.name,
                  checked: department.checked,
                }))}
                onChange={updateDepartment}
                type="selectMultipleCheckbox"
                classNameValue="text-sm h-10 px-4 flex items-center justify-start"
              />
            </div>

            {/* Mission period (temporal) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
              <FilterDropdown
                maxWidth="w-full"
                value={
                  missionPeriod.filter((p) => p.checked).length > 0
                    ? missionPeriod
                        .filter((p) => p.checked)
                        .map((p) => p.name)
                        .join(', ')
                    : 'Sélectionner'
                }
                list={missionPeriod.map((p) => ({
                  value: p.type,
                  label: p.name,
                  checked: p.checked,
                }))}
                onChange={updateMissionPeriod}
                type="selectMultipleCheckbox"
                classNameValue="text-sm h-10 px-4 flex items-center justify-start"
              />
            </div>

            {/* Mission status (action) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Visibilité</label>
              {renderMissionStatusFilter(tempStatusSearch, setTempStatusSearch, true)}
            </div>

            {/* Employee assignment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Admin assigné</label>
              <FilterDropdown
                maxWidth="w-full"
                value={
                  employeesSearch.filter((employee) => employee.checked)
                    .length > 0
                    ? employeesSearch
                        .filter((employee) => employee.checked)
                        .map(
                          (employee) =>
                            `${employee.firstname} ${employee.lastname}`
                        )
                        .join(', ')
                    : 'Sélectionner'
                }
                list={buildEmployeeFilterList(employeesSearch)}
                onChange={updateEmployeeSearch}
                type="selectMultipleCheckbox"
                isSearchable
                additionalClassNameDropDown="text-gray-900"
                classNameValue="text-sm h-10 px-4 flex items-center justify-start"
                additionalClassName="h-10"
              />
            </div>

            <Button
              color={mobileHasFilterChanges ? 'SECONDARY' : 'DISABLED'}
              size={'FULL'}
              disabled={!mobileHasFilterChanges}
              onClick={handleMobileSaveFilters}
            >
              <SaveIcon wh={20} color={mobileHasFilterChanges ? '#374151' : '#9CA3AF'} />
              Sauvegarder les filtres
            </Button>

            {/* Apply button */}
            <Button
              color={'PRIMARY'}
              size={'FULL'}
              onClick={applyFilters}
            >
              Appliquer
            </Button>
          </div>
        </BottomSheet>
        <div className="flex flex-col w-full h-full overflow-hidden mission-container">
        {/* Results and details display section */}
          <div className="flex flex-1 w-full h-full gap-3 overflow-y-auto overflow-x-hidden p-3 mission-container-body">
          {searchMissionResultWaiting ? (
            // Display loading state with animated placeholders
            <div className="flex-1 h-full w-full lg:w-[555px] lg:max-w-[555px] animate-pulse">
              <div className="flex flex-col gap-4 max-h-full w-full h-full">
                <div className="flex flex-col gap-3 overflow-y-auto h-full">
                  {[1, 2, 3, 4, 5].map((i, index) => (
                    <div
                      key={index}
                      className="flex flex-col gap-1.5 border border-gray-100 rounded-lg p-4 cursor-pointer hover:bg-gray-50"
                    >
                      <div className="flex gap-1.5 justify-between items-center">
                        <div className="flex items-center gap-1 flex-1 min-w-0">
                          <div className="truncate">
                            <div className="h-4 bg-gray-200 rounded w-56"></div>
                          </div>
                          <div>
                            <div className="h-4 bg-gray-200 rounded w-8"></div>
                          </div>
                        </div>
                        <div className="flex gap-1 items-center text-sm">
                          <div className="px-1.5">
                            <div className="h-6 bg-gray-200 rounded w-6"></div>
                          </div>
                          <Button size="SMI" color="DEFAULT">
                            <div className="h-6 bg-gray-200 rounded w-6"></div>
                          </Button>
                          <Button size="SMI" color="DEFAULT">
                            <div className="h-6 bg-gray-200 rounded w-6"></div>
                          </Button>
                          <Button size="SMI" color="DEFAULT">
                            <div className="h-6 bg-gray-200 rounded w-6"></div>
                          </Button>
                        </div>
                      </div>
                      <div className="text-sm flex items-center justify-between gap-4">
                        <div className="flex flex-col gap-0.5 text-gray-700 w-1/2">
                          <div className="flex gap-1 items-center">
                            <div>
                              <Calendar wh={18} color="#374151" />
                            </div>
                            <div className="h-4 bg-gray-200 rounded w-56"></div>
                          </div>
                          <div className="flex gap-1 items-center">
                            <div>
                              <Building wh={18} color="#374151" />
                            </div>
                            <div className="flex gap-1">
                              <div className="h-4 bg-gray-200 rounded w-56"></div>
                            </div>
                          </div>
                          <div className="flex gap-1 items-center">
                            <div>
                              <Signer wh={18} color="#374151" />
                            </div>
                            <div className="h-4 bg-gray-200 rounded w-56"></div>
                          </div>
                          <div className="flex gap-1 items-center">
                            <div>
                              <ReferenceIcon wh={18} color="#374151" />
                            </div>
                            <div className="h-4 bg-gray-200 rounded w-56"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : searchMissionResult.length > 0 ? (
            // Display the mission results list and detail panel
            <MissionDisplay
              searchMissionResult={searchMissionResult}
              search={search}
              editMission={editMission}
              setEditMission={setEditMission}
              selectedMissions={selectedMissions}
              employees={employees}
              openCompanyInfo={openCompanyInfo}
              openTempWorkerInfo={openTempWorkerInfo}
              handleSelectMission={handleSelectMission}
              shouldShowTrashButton={shouldShowTrashButton}
              handleDeleteMission={handleDeleteMission}
              handleCancelMission={handleCancelMission}
              updateEmployeeForMission={updateEmployeeForMission}
              currentPage={currentPage}
              countPerPage={countPerPage}
              countTotal={countTotal}
              totalPages={totalPages}
              setCurrentPage={setCurrentPage}
              setSearchMissionResult={setSearchMissionResult}
            />
          ) : hiddenByFiltersCount > 0 ? (
            <EmptyState
              type="mission_search"
              title={
                hiddenByFiltersCount > 1
                  ? `${hiddenByFiltersCount} missions masquées par vos filtres`
                  : '1 mission masquée par vos filtres'
              }
              description={
                hiddenByFiltersCount > 1
                  ? 'Des missions correspondent à votre recherche mais sont masquées par les filtres actuels. Réinitialisez-les pour les voir.'
                  : 'Une mission correspond à votre recherche mais est masquée par les filtres actuels. Réinitialisez-les pour la voir.'
              }
              action={{ label: 'Réinitialiser les filtres', onClick: resetFiltersToDefault }}
            />
          ) : (
            <EmptyState type="mission_search" />
          )}

          {/* Display a fixed footer for bulk mission assignment if any mission is selected */}
          {selectedMissions.length > 0 && (
            <div className="fixed bottom-2 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white p-1.5 rounded">
              <div className="flex items-center text-sm h-10">
                <div className="flex gap-1 items-center py-2 px-3">
                  <div className="w-fit">
                    <Badge type="WARNING">{selectedMissions.length}</Badge>
                  </div>
                  sélectionnée{selectedMissions.length > 1 ? 's' : ''}
                </div>
                <div className="w-0.5 h-1/2 self-center bg-white" />
                <div className="flex gap-1 items-center">
                  <StyledInput
                    type="selectMultipleRadio"
                    value={
                      <div className="flex gap-1">
                        <PersonIcon wh={20} color="#FFF" />
                        <div className="text-white">Attribuer à</div>
                      </div>
                    }
                    list={employees.map((employee) => ({
                      value: employee._id,
                      label: (
                        <div className="flex gap-1.5 items-center">
                          {employee._id !== 'unassign' && (
                            <div
                              className="rounded-full shrink-0 w-[24px] h-[24px] flex items-center justify-center text-[10px] text-white"
                              style={{
                                backgroundColor: generateEmployeeColor(
                                  employee.firstname,
                                  employee.lastname
                                ),
                              }}
                            >
                              {`${
                                employee.firstname?.charAt(0).toUpperCase() ||
                                ''
                              }${
                                employee.lastname?.charAt(0).toUpperCase() || ''
                              }`}
                            </div>
                          )}
                          <div>
                            {employee.firstname} {employee.lastname}
                          </div>
                        </div>
                      ),
                      checked: employee.checked,
                    }))}
                    className={'rounded hover:bg-gray-900'}
                    additionalClassNameDropDown={'text-gray-900'}
                    showChevron={false}
                    showCheck={false}
                    onChange={updateEmployee}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      </>
    </PageForm>
  );
};

export default MissionPeriod;
